import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { BuildingIcon } from 'lucide-react';

import type { EstatesOptions } from '@/api/estate-options';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { projectAtom } from '@/features/navigation/atoms/projectAtom';
import { getDate } from '@/utils/getDate';
import { getRenderValue } from '@/utils/getEmptyVisualValue';

import { StatusType } from '../constants/status-type';
import { EstateAvailabilityBadge } from './estate-availability-badge';
import type { EstateCardProps } from './estate-grid-card';

type EstateAdditionalExtendedInfoProps = {
  creator: string;
  createdAt: string;
};

export type EstateLongCardProps = EstateCardProps &
  EstateAdditionalExtendedInfoProps & {
    thumbnailId: string;
    externalId?: string | null;
    marketType?: string;
    propertyType?: string;
    usageType?: string;
    estateOptions?: EstatesOptions;
    projects?: {
      id: string;
      name: string;
    } | null;
  };

export const EstatesListCard = ({ estateOptions, ...props }: EstateLongCardProps) => {
  const { t } = useTranslation();
  const setProject = useSetAtom(projectAtom);

  const params =
    props.propertyLevel === PROPERTY_LEVELS.PROJECT
      ? { projectId: props.id, estateId: '' }
      : { projectId: '', estateId: props.id };
  const linkTo =
    props.propertyLevel === PROPERTY_LEVELS.PROJECT ? '/projects/$projectId' : '/estates/$estateId';

  const { propertyType, marketType } = useMemo(() => {
    let resolvedPropertyType = props.propertyType;
    let resolvedMarketType = props.marketType;

    if (resolvedPropertyType && estateOptions?.propertyType) {
      resolvedPropertyType =
        estateOptions.propertyType.find(({ id }) => id === resolvedPropertyType)?.title ??
        resolvedPropertyType;
    }

    if (resolvedMarketType && estateOptions?.marketingType) {
      resolvedMarketType =
        estateOptions.marketingType.find(({ id }) => id === resolvedMarketType)?.title ??
        resolvedMarketType;
    }

    return {
      propertyType: resolvedPropertyType,
      marketType: resolvedMarketType,
    };
  }, [props.propertyType, props.marketType, estateOptions]);
  const handleClick = () => {
    if (typeof window !== 'undefined') {
      const scrollKey = `scroll:${window.location.pathname}${window.location.search}`;
      const mainElement =
        typeof document !== 'undefined' ? document.querySelector<HTMLElement>('main.flex-1') : null;

      const scrollTop = mainElement ? mainElement.scrollTop : window.scrollY;

      const state = {
        scrollTop,
        estateId: String(props.id),
        pathname: window.location.pathname,
        search: window.location.search,
      };

      window.sessionStorage.setItem(scrollKey, JSON.stringify(state));
    }

    setProject(props.projects ?? null);
  };

  return (
    <Link to={linkTo} params={params} onClick={handleClick}>
      <div
        data-estate-id={props.id}
        className="flex items-stretch border-t border-slate-200 px-2 py-4 text-sm text-primary-dark transition-colors duration-150 hover:bg-slate-50 sm:px-4"
      >
        <div className="ml-4 flex min-w-[520px] flex-[2.5] gap-4">
          <div className="relative h-[80px] w-[120px] flex-shrink-0 overflow-hidden rounded bg-slate-100 sm:h-[80px] sm:w-[120px]">
            <EstateImage
              className="h-full w-full object-cover"
              {...(props.thumbnailId ? { thumbnailId: props.thumbnailId } : {})}
              iconSize={28}
            />
            {props.propertyLevel === PROPERTY_LEVELS.PROJECT ? (
              <div className="absolute bottom-1 left-1 flex h-7 w-7 items-center justify-center rounded-full bg-[#E0F7FA] text-[hsl(var(--primary-color-light))]">
                <BuildingIcon size={16} />
              </div>
            ) : null}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {propertyType ? (
                <div className="rounded-2xl bg-[#07B1BE1F] px-3 py-[2px] text-xs font-medium text-primary-dark">
                  {propertyType}
                </div>
              ) : null}
              {marketType ? (
                <div className="rounded-2xl bg-[#07B1BE1F] px-3 py-[2px] text-xs font-medium text-primary-dark">
                  {marketType}
                </div>
              ) : null}
              {props.status ? (
                <div className="rounded-2xl bg-[#07B1BE1F] px-3 py-[2px] text-xs font-medium text-primary-dark">
                  {props.status === StatusType.draft ? t('draft') : t('public')}
                </div>
              ) : null}
              {props.propertyLevel === PROPERTY_LEVELS.OBJECT ? (
                <EstateAvailabilityBadge availability={props.availability ?? null} />
              ) : null}
            </div>
            <div className="truncate text-sm font-semibold leading-tight">
              {getRenderValue(props.name)}
            </div>
            {props.address?.address ? (
              <div className="truncate text-xs text-gray-700">{props.address?.address}</div>
            ) : null}
          </div>
        </div>

        {/* Price */}
        <div className="flex w-[120px] items-center justify-start">
          {getRenderValue(props.price, 1)}
        </div>

        {/* Area */}
        <div className="flex w-[100px] items-center justify-start">
          {props.livingArea || props.livingArea === 0
            ? `${props.livingArea} m²`
            : getRenderValue(props.livingArea, 1)}
        </div>

        {/* Rooms */}
        <div className="flex w-[60px] items-center justify-start">
          {getRenderValue(props.roomsCount, 1)}
        </div>

        {/* Creator */}
        <div className="flex w-[120px] items-center justify-start whitespace-nowrap">
          {getRenderValue(props.creator, 1)}
        </div>

        {/* Created */}
        <div className="flex w-[90px] items-center justify-start whitespace-nowrap">
          {getDate(props.createdAt)}
        </div>

        {/* Actions */}
        {props.btnActionRender ? (
          <div className="flex w-[80px] flex-1 items-center justify-end">
            {props.btnActionRender(props)}
          </div>
        ) : null}
      </div>
    </Link>
  );
};
